import { Typography } from '@mui/material';
import { getProducts, sudoLogin } from 'API/account';
import { bindAccountToAgency, getAgencyAdmins, unbindAccountFromAgency } from 'API/agency-management';
import { refreshTokenCall } from 'API/APIs/APIv1';
import {
  addCompany,
  changeCompanyData,
  changeOwnCompanyData,
  deleteCompany,
  getCompanyData,
  getLicensesByCompany,
  getUsersByCompany2,
} from 'API/company';
import {
  cancelLicense,
  createLicense,
  deleteLicense,
  editLicense,
  getAvailableProducts,
  getFullLicense,
} from 'API/license';
import { createCustomerPortalSession } from 'API/payment';
import NotificationPreferencesSection from 'features/engagement/notifications/notification-preferences';
import CancelSubscriptionDialog from 'features/engagement/subscription/cancel-dialog';
import { addAccount, changePassword, changeUserInfo, deleteAccount, editAccount, getAllUsers, getUserInfo, resendInvitation } from 'API/user';
import { addMonths } from 'date-fns';
import { ErrorMessages, Products, Roles, Routes, ZoomLevels } from 'enums';
import { getAccountTabRoute, useAccountManagementTabs } from 'features/account/account-management/account-management-tabs';
import {
  AccountManagementTabs,
  TenantScopes,
  User,
  ViewModes,
} from 'features/account/account-management/account-management.types';
import AddLicenseToUserDialog from 'features/account/account-management/licenses/add-license-to-user-dialog';
import AdminLicenseDialog from 'features/account/account-management/licenses/admin-license-dialog';
import CancelLicenseDialog from 'features/account/account-management/licenses/cancel-license-dialog';
import DeleteLicenseDialog from 'features/account/account-management/licenses/delete-license-dialog';
import LinkedinAccountPreviewDialog from 'features/account/account-management/licenses/linkedin-account-preview-dialog';
import ManageLicenseUsersDialog from 'features/account/account-management/licenses/manage-license-users-dialog';
import useLicenses from 'features/account/account-management/licenses/use-licenses';
import ManageCompany from 'features/account/account-management/manage-company';
import ManageLicenses from 'features/account/account-management/manage-licenses';
import ManageProfile from 'features/account/account-management/manage-profile';
import ManageTenants from 'features/account/account-management/manage-tenants';
import ManageUsers from 'features/account/account-management/manage-users';
import AdminLoginDialog from 'features/account/account-management/tenants/admin-login-dialog';
import AgencyBindingDialog from 'features/account/account-management/tenants/agency-binding-dialog';
import CreateAndEditTenantDialog from 'features/account/account-management/tenants/create-and edit-tenant-dialog';
import DeleteTenantDialog from 'features/account/account-management/tenants/delete-tenant-dialog';
import useCompanies from 'features/account/account-management/tenants/use-companies';
import useTenantLicenses from 'features/account/account-management/tenants/use-tenant-licenses';
import useTenantUsers from 'features/account/account-management/tenants/use-tenant-users';
import useOwnCompanyInfoForm from 'features/account/account-management/use-own-company-info-form';
import usePasswordForm, { PasswordErrors } from 'features/account/account-management/use-password-form';
import useSettingsForm from 'features/account/account-management/use-settings-form';
import CreateAndEditUserDialog from 'features/account/account-management/users/create-and edit-user-dialog';
import DeleteUserDialog from 'features/account/account-management/users/delete-user-dialog';
import ManageUserLicensesDialog from 'features/account/account-management/users/manage-user-licenses-dialog';
import PasswordLinkDialog from 'features/account/account-management/users/password-link-dialog';
import useUsers from 'features/account/account-management/users/use-users';
import { useUserDialogs } from 'features/account/account-management/hooks/use-user-dialogs';
import { useTenantDialogs } from 'features/account/account-management/hooks/use-tenant-dialogs';
import { useLicenseDialogs } from 'features/account/account-management/hooks/use-license-dialogs';
import usePrices from 'features/payment/use-prices';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import { useExtensionAuth } from 'hooks/use-extension-auth';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import { changeLanguage } from 'i18next';
import jwt from 'jsonwebtoken';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import useErrorStore, { type CustomError } from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import { Company } from 'types/company.types';
import { License } from 'types/licenses.types';
import { UserInfo } from 'types/user.types';
import { trackPurchaseComplete } from 'utils/tracking';
import isEmail from 'validator/lib/isEmail';

/** Headline + subline shown above the content area for each tab (i18n keys). */
const TAB_HEADERS: Record<AccountManagementTabs, { title: string; subtitle: string }> = {
  [AccountManagementTabs.PROFILE]: { title: 'myProfile', subtitle: 'myProfileSubtitle' },
  [AccountManagementTabs.NOTIFICATIONS]: { title: 'engPrefsTab', subtitle: 'engPrefsSubtitle' },
  [AccountManagementTabs.COMPANY]: { title: 'myCompany', subtitle: 'myCompanySubtitle' },
  [AccountManagementTabs.TENANTS]: { title: 'myClients', subtitle: 'myClientsSubtitle' },
  [AccountManagementTabs.USERS]: { title: 'myUsers', subtitle: 'myUsersSubtitle' },
  [AccountManagementTabs.LICENSES]: { title: 'myLicenses', subtitle: 'myLicensesSubtitle' },
  [AccountManagementTabs.ADMIN_PANEL]: { title: 'adminPanel', subtitle: 'adminPanelSubtitle' },
};

const AccountManagement: React.FC = () => {
  const { classes } = useStyles();
  const { tenant, role: userRole, email: userEmail } = useUserData();
  const { t } = useTranslation();

  const { extensionSignIn } = useExtensionAuth();
  const signIn = useUserStore((state) => state.signIn);
  const setToken = useUserStore((state) => state.setToken);
  const navigate = useNavigate();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const saveUserInformationPersistent = useUserStore((state) => state.saveUserInformationPersistent);

  // Der Reiter steht in der URL (/account/<tab>), damit das linke Kontextpanel ihn markiert.
  const location = useLocation();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const allowedTabs = useAccountManagementTabs();
  const activeTab =
    allowedTabs.find((tab) => tab.value === tabParam)?.value ?? AccountManagementTabs.PROFILE;
  const setActiveTab = (tab: AccountManagementTabs) => {
    navigate({ pathname: getAccountTabRoute(tab), search: location.search }, { replace: true });
  };

  const { firstName, lastName, language, email, onUserInfoChange } = useSettingsForm();
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [firstNameInvalid, setFirstNameInvalid] = useState(false);
  const [lastNameInvalid, setLastNameInvalid] = useState(false);
  const [updateUserInfoLoading, setUpdateUserInfoLoading] = useState(false);

  const { oldPassword, newPassword, newPasswordRepeated, onPasswordChange, resetPasswordForm } = usePasswordForm();
  const [passwordInvalid, setPasswordInvalid] = useState<PasswordErrors | false>(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const { company, street, zip, city, country, handleCompanyInfoChange, countryOptions, handleCountryChange } = useOwnCompanyInfoForm();
  const [companyNameInvalid, setCompanyNameInvalid] = useState(false);
  const [cityInvalid, setCityInvalid] = useState(false);
  const [companyInfoLoading, setCompanyInfoLoading] = useState(false);

  const {
    users,
    total: usersTotal,
    loading: usersLoading,
    itemsPerPage: usersItemsPerPage,
    page: usersPage,
    handleChangePage: usersHandleChangePage,
    searchParam: usersSearchParam,
    handleSearchParamChange: usersHandleSearchParamChange,
    searchKeyDown: usersSearchKeyDown,
    searchKeyUp: usersSearchKeyUp,
    handleClearSearchField: usersHandleClearSearchField,
    fetchUsers,
    resetUsers,
  } = useUsers();

  // User dialog states (consolidated from useUserDialogs hook)
  const {
    createAndEditUserDialog,
    setCreateAndEditUserDialog,
    addLicenseToUserDialog,
    setAddLicenseToUserDialog,
    manageUserLicensesDialog,
    setManageUserLicensesDialog,
    deleteUserDialog,
    setDeleteUserDialog,
  } = useUserDialogs();

  const [openTenantId, setOpenTenantId] = useState<string | null>(null);
  const [tenantScope, setTenantScope] = useState<TenantScopes>(TenantScopes.USERS);

  const {
    companies,
    total: tenantsTotal,
    loading: tenantsLoading,
    itemsPerPage: tenantsItemsPerPage,
    page: tenantsPage,
    searchParam: tenantsSearchParam,
    searchType: tenantsSearchType,
    sortBy: tenantsSortBy,
    handleSearchParamChange: tenantsHandleSearchParamChange,
    searchKeyUp: tenantsSearchKeyUp,
    searchKeyDown: tenantsSearchKeyDown,
    handleClearSearchField: tenantsHandleClearSearchField,
    handleSearchTypeChange: tenantsHandleSearchTypeChange,
    handleChangePage: tenantsHandleChangePage,
    handleSortByChange: tenantsHandleSortByChange,
    fetchCompanies,
    resetCompanies,
  } = useCompanies(resetOpenTenantId);

  // Tenant dialog states (consolidated from useTenantDialogs hook)
  const {
    createAndEditTenantDialog,
    setCreateAndEditTenantDialog,
    deleteTenantDialog,
    setDeleteTenantDialog,
    agencyBindingDialog,
    setAgencyBindingDialog,
    adminLoginDialog,
    setAdminLoginDialog,
  } = useTenantDialogs();

  const {
    tenantUsers,
    total: tenantUsersTotal,
    loading: tenantUsersLoading,
    itemsPerPage: tenantUsersItemsPerPage,
    page: tenantUsersPage,
    handleChangePage: tenantUsersHandleChangePage,
    fetchTenantUsers,
  } = useTenantUsers(openTenantId, tenantScope, tenantsSearchType, tenantsSearchParam);

  const {
    tenantLicenses,
    total: tenantLicensesTotal,
    loading: tenantLicensesLoading,
    paginationLoading: tenantLicensesPaginationLoading,
    itemsPerPage: tenantLicensesItemsPerPage,
    page: tenantLicensesPage,
    handleChangePage: tenantLicensesHandleChangePage,
    fetchTenantLicenses,
  } = useTenantLicenses(openTenantId, tenantScope);

  const {
    licenses,
    total: licensesTotal,
    loading: licensesLoading,
    searchParam: licensesSearchParam,
    searchKeyDown: licensesSearchKeyDown,
    searchKeyUp: licensesSearchKeyUp,
    handleSearchParamChange: licensesHandleSearchParamChange,
    itemsPerPage: licensesItemsPerPage,
    page: licensesPage,
    handleChangePage: licensesHandleChangePage,
    handleClearSearchField: licensesHandleClearSearchField,
    fetchLicenses,
    resetLicenses,
  } = useLicenses();

  // License dialog states (consolidated from useLicenseDialogs hook)
  const {
    linkedinAccountPreviewDialog,
    setLinkedinAccountPreviewDialog,
    manageLicenseUsersDialog,
    setManageLicenseUsersDialog,
    cancelLicenseDialog,
    setCancelLicenseDialog,
    adminLicenseDialog,
    setAdminLicenseDialog,
    deleteLicenseDialog,
    setDeleteLicenseDialog,
  } = useLicenseDialogs();

  const [successAlert, setSuccessAlert] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [passwordLinkDialog, setPasswordLinkDialog] = useState<{ open: boolean; resetLink: string; userName: string }>({
    open: false,
    resetLink: '',
    userName: '',
  });
  const [resendingInvitationUserId, setResendingInvitationUserId] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const stripeProduct: Products | null = searchParams.get('product') as Products;
  const sessionId: string | null = searchParams.get('session_id');
  const setProducts = useUserStore((state) => state.setProducts);
  const setUserLicenses = useUserStore((state) => state.setLicenses);

  const refreshGlobalLicenses = async () => {
    const res = await getFullLicense();
    if (Array.isArray(res)) {
      setUserLicenses(res);
    }
  };

  // Get pricing data for accurate tracking
  const { prices } = usePrices();

  // Die Liste des aktiven Reiters laden — beim Wechsel über das Kontextpanel UND beim direkten
  // Öffnen einer Reiter-URL (z. B. /account/admin-panel). Die Listen laden sich nicht selbst;
  // startete der Ref mit dem aktiven Reiter, blieb ein direkt geöffneter Reiter leer.
  const previousTab = useRef<AccountManagementTabs | null>(null);
  useEffect(() => {
    if (previousTab.current === activeTab) return;
    previousTab.current = activeTab;

    if (activeTab === AccountManagementTabs.USERS) {
      resetUsers();
    }
    if (activeTab === AccountManagementTabs.TENANTS || activeTab === AccountManagementTabs.ADMIN_PANEL) {
      resetCompanies();
    }
    if (activeTab === AccountManagementTabs.LICENSES) {
      resetLicenses();
    }
  }, [activeTab]);

  // /account ohne Reiter oder mit unbekanntem Reiter → auf die Profil-URL, damit das Panel sie markiert.
  // Ein bekannter, aber (noch) nicht erlaubter Reiter bleibt stehen: die Rolle kann erst nachladen.
  useEffect(() => {
    const knownTab = Object.values(AccountManagementTabs).includes(tabParam as AccountManagementTabs);
    if (!knownTab && !searchParams.get('tab')) {
      setActiveTab(AccountManagementTabs.PROFILE);
    }
  }, [tabParam]);

  useEffect(() => {
    if (userRole === Roles.ADMIN || userRole === Roles.AGENCY || userRole === Roles.OWNER) {
      resetOpenTenantId();
    }
  }, [userRole, activeTab]);

  // Alte Deep-Links mit ?tab= auf /account/<tab> umlenken (nur erlaubte Reiter, sonst Profil).
  useEffect(() => {
    const legacyTab = searchParams.get('tab');
    if (!legacyTab) return;
    const target = allowedTabs.find((tab) => tab.value === legacyTab)?.value ?? AccountManagementTabs.PROFILE;
    searchParams.delete('tab');
    const search = searchParams.toString();
    navigate({ pathname: getAccountTabRoute(target), search: search ? `?${search}` : '' }, { replace: true });
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case AccountManagementTabs.NOTIFICATIONS:
        return <NotificationPreferencesSection />;
      case AccountManagementTabs.PROFILE:
        return (
          <ManageProfile
            firstName={firstName}
            lastName={lastName}
            language={language}
            email={email}
            isEmailInvalid={isEmailInvalid}
            firstNameInvalid={firstNameInvalid}
            lastNameInvalid={lastNameInvalid}
            updateUserInfoLoading={updateUserInfoLoading}
            oldPassword={oldPassword}
            newPassword={newPassword}
            newPasswordRepeated={newPasswordRepeated}
            passwordInvalid={passwordInvalid}
            passwordLoading={passwordLoading}
            onUserInfoChange={onUserInfoChange}
            saveUserInformationChanges={saveUserInformationChanges}
            onPasswordChange={onPasswordChange}
            savePasswordChanges={savePasswordChanges}
          />
        );
      case AccountManagementTabs.COMPANY:
        return (
          <ManageCompany
            company={company}
            street={street}
            zip={zip}
            city={city}
            country={country}
            handleCompanyInfoChange={handleCompanyInfoChange}
            countryOptions={countryOptions}
            handleCountryChange={handleCountryChange}
            companyNameInvalid={companyNameInvalid}
            cityInvalid={cityInvalid}
            companyInfoLoading={companyInfoLoading}
            saveOwnCompanyChanges={saveOwnCompanyChanges}
          />
        );
      case AccountManagementTabs.ADMIN_PANEL:
      case AccountManagementTabs.TENANTS:
        return (
          <ManageTenants
            userRole={userRole}
            tenants={companies}
            loading={tenantsLoading}
            total={tenantsTotal}
            itemsPerPage={tenantsItemsPerPage}
            page={tenantsPage}
            handleChangePage={tenantsHandleChangePage}
            searchParam={tenantsSearchParam}
            searchType={tenantsSearchType}
            sortBy={tenantsSortBy}
            openTenantId={openTenantId}
            tenantScope={tenantScope}
            tenantUsers={tenantUsers}
            tenantUsersTotal={tenantUsersTotal}
            tenantUsersLoading={tenantUsersLoading}
            tenantUsersItemsPerPage={tenantUsersItemsPerPage}
            tenantUsersPage={tenantUsersPage}
            tenantLicenses={tenantLicenses}
            tenantLicensesTotal={tenantLicensesTotal}
            tenantLicensesLoading={tenantLicensesLoading}
            tenantLicensesPaginationLoading={tenantLicensesPaginationLoading}
            tenantLicensesItemsPerPage={tenantLicensesItemsPerPage}
            tenantLicensesPage={tenantLicensesPage}
            handleSearchParamChange={tenantsHandleSearchParamChange}
            searchKeyDown={tenantsSearchKeyDown}
            searchKeyUp={tenantsSearchKeyUp}
            handleClearSearchField={tenantsHandleClearSearchField}
            handleSearchTypeChange={tenantsHandleSearchTypeChange}
            handleSortByChange={tenantsHandleSortByChange}
            handleOpenDeleteTenantDialog={handleOpenDeleteTenantDialog}
            handleOpenCreateTenantDialog={handleOpenCreateTenantDialog}
            handleOpenEditTenantDialog={handleOpenEditTenantDialog}
            handleOpenTenant={handleOpenTenant}
            handleToggleTenantScope={handleToggleTenantScope}
            tenantUsersHandleChangePage={tenantUsersHandleChangePage}
            handleOpenCreateUserDialog={handleOpenCreateUserDialog}
            handleOpenEditUserDialog={handleOpenEditUserDialog}
            handleOpenDeleteUserDialog={handleOpenConfirmDeleteUserDialog}
            handleResendInvitation={handleResendInvitation}
            resendingInvitationUserId={resendingInvitationUserId}
            handleOpenAdminSudoLoginDialog={handleOpenAdminSudoLoginDialog}
            tenantLicensesHandleChangePage={tenantLicensesHandleChangePage}
            handleOpenAdminCreateLicenseDialog={handleOpenAdminCreateLicenseDialog}
            handleOpenAdminEditLicenseDialog={handleOpenAdminEditLicenseDialog}
            handleOpenDeleteLicenseDialog={handleOpenDeleteLicenseDialog}
            handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog}
            handleOpenAgencyDialog={handleOpenAgencyDialog}
          />
        );
      case AccountManagementTabs.USERS:
        return (
          <ManageUsers
            users={users}
            loading={usersLoading}
            total={usersTotal}
            itemsPerPage={usersItemsPerPage}
            page={usersPage}
            handleChangePage={usersHandleChangePage}
            searchParam={usersSearchParam}
            handleSearchParamChange={usersHandleSearchParamChange}
            searchKeyDown={usersSearchKeyDown}
            searchKeyUp={usersSearchKeyUp}
            handleClearSearchField={usersHandleClearSearchField}
            handleOpenConfirmDeleteUserDialog={handleOpenConfirmDeleteUserDialog}
            handleOpenCreateUserDialog={handleOpenCreateUserDialog}
            handleOpenEditUserDialog={handleOpenEditUserDialog}
            handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog}
            handleResendInvitation={handleResendInvitation}
            resendingInvitationUserId={resendingInvitationUserId}
          />
        );
      case AccountManagementTabs.LICENSES:
        return (
          <ManageLicenses
            licenses={licenses}
            total={licensesTotal}
            loading={licensesLoading}
            itemsPerPage={licensesItemsPerPage}
            page={licensesPage}
            handleChangePage={licensesHandleChangePage}
            handleSearchParamChange={licensesHandleSearchParamChange}
            searchKeyDown={licensesSearchKeyDown}
            searchKeyUp={licensesSearchKeyUp}
            handleClearSearchField={licensesHandleClearSearchField}
            searchParam={licensesSearchParam}
            handleOpenManageLicenseUsersDialog={handleOpenManageLicenseUsersDialog}
            handleManageSubscription={handleManageSubscription}
            handleOpenCancelLicenseDialog={handleOpenCancelLicenseDialog}
            handleOpenCancelSubscription={handleOpenCancelSubscription}
          />
        );
      default:
        return null;
    }
  };

  /**
   * Success Dialog handlers
   */

  const handleOpenSuccessAlert = (message: string) => {
    setSuccessAlert({ open: true, message: message });
  };

  const handleCloseSuccessAlert = () => {
    setSuccessAlert({ open: false, message: '' });
  };

  /**
   * Profile handlers
   */

  useEffect(() => {
    setIsEmailInvalid(false);
  }, [email]);

  useEffect(() => {
    setFirstNameInvalid(false);
  }, [firstName]);

  useEffect(() => {
    setLastNameInvalid(false);
  }, [lastName]);

  const saveUserInformationChanges = async () => {
    if (!isEmail(email)) {
      setIsEmailInvalid(true);
      return;
    }

    setUpdateUserInfoLoading(true);

    const newFirstName = firstName.trim();
    const newLastName = lastName.trim();
    const newEmail = email.trim();

    const apiPayload = {
      firstName: newFirstName,
      lastName: newLastName,
      email: newEmail,
      language,
    };

    const res = await changeUserInfo(apiPayload);

    if (res.error) {
      const validationErrorMessage = (res as any).error?.errors?.body[0]?.message;
      if (validationErrorMessage === ErrorMessages.EMAIL_VALIDATION_ERROR) {
        setIsEmailInvalid(true);
      } else if (validationErrorMessage === ErrorMessages.FIRSTNAME_LENGTH_VALIDATION_ERROR) {
        setFirstNameInvalid(true);
      } else if (validationErrorMessage === ErrorMessages.LASTNAME_LENGTH_VALIDATION_ERROR) {
        setLastNameInvalid(true);
      } else {
        handleOpenErrorDialog(res.error, '/users/me', 'patch');
      }
      setUpdateUserInfoLoading(false);
      return;
    }

    localStorage.setItem('i18nextLng', language);
    changeLanguage(language);

    const userDataPayload = {
      firstName: newFirstName,
      lastName: newLastName,
      email: newEmail,
      language,
    };
    saveUserInformationPersistent(userDataPayload);

    handleOpenSuccessAlert(t('userInformationUpdatedSuccessfully'));
    setUpdateUserInfoLoading(false);
  };

  /**
   * Password handlers
   */

  useEffect(() => {
    setPasswordInvalid(false);
  }, [oldPassword, newPassword, newPasswordRepeated]);

  const savePasswordChanges = async () => {
    if (newPassword.length <= 5) {
      setPasswordInvalid(PasswordErrors.NEW_TOO_SHORT);
      return;
    }

    if (newPassword.length > 50) {
      setPasswordInvalid(PasswordErrors.NEW_TOO_LONG);
      return;
    }

    if (!_.isEqual(newPassword, newPasswordRepeated)) {
      setPasswordInvalid(PasswordErrors.REPEATED_NO_MATCH);
      return;
    }

    setPasswordLoading(true);
    const res = await changePassword({ oldPassword, newPassword });

    if (res.error) {
      if (res.error.message === 'Validation Error' || res.error.message === 'wrong password') {
        setPasswordInvalid(PasswordErrors.OLD_INVALID);
      } else {
        handleOpenErrorDialog(res.error, '/users/me/password', 'post');
      }

      setPasswordLoading(false);
      return;
    }

    resetPasswordForm();
    handleOpenSuccessAlert(t('passwordUpdatedSuccessfully'));
    setPasswordLoading(false);
  };

  /**
   * Company handlers
   */

  useEffect(() => {
    setCompanyNameInvalid(false);
  }, [company]);

  useEffect(() => {
    setCityInvalid(false);
  }, [city]);

  const saveOwnCompanyChanges = async () => {
    if (company.trim().length > 30) {
      setCompanyNameInvalid(true);
      return;
    }

    setCompanyInfoLoading(true);

    const updatePayload: Company = new Company({
      name: company.trim(),
      addressLine1: street.trim(),
      postalCode: zip.trim(),
      city: city.trim(),
      country: country?.value || undefined,
    });

    const res = await changeOwnCompanyData(updatePayload);

    if (res.error) {
      const validationErrorMessage = (res as any).error?.errors?.body[0]?.message;
      if (validationErrorMessage === ErrorMessages.CITY_NAME_LENGTH_VALIDATION_ERROR) {
        setCityInvalid(true);
      } else {
        handleOpenErrorDialog(res.error, '/tenants/current', 'patch');
      }
      setCompanyInfoLoading(false);
      return;
    }

    handleOpenSuccessAlert(t('companyInformationUpdatedSuccessfully'));
    setCompanyInfoLoading(false);
  };

  /**
   * Manage users handlers
   */

  /**
   * Delete user handlers
   */

  const handleOpenConfirmDeleteUserDialog = (user: User) => {
    return () => {
      (async () => {
        const tenantId = typeof user.tenant === 'string' ? user.tenant : user.tenant._id;
        const res = await getUsersByCompany2(tenantId ?? tenant);
        if (!res.error && res.users) {
          const tenantUsers = _.map(res.users, (user) => ({ value: user.globalUser, label: `${user.firstname} ${user.lastname}` }));
          setDeleteUserDialog((prev) => ({ ...prev, tenantUsers }));
        } else {
          handleOpenErrorDialog(res?.error, '/users/legacy/tenant-users/:userId', 'get');
        }
      })();

      setDeleteUserDialog({ ...deleteUserDialog, open: true, user });
    };
  };

  const handleCloseConfirmDeleteUserDialog = () => {
    setDeleteUserDialog({ open: false, loading: false, user: null, deleteData: true, toMoveUser: undefined, tenantUsers: [] });
  };

  const handleDeleteUserDataChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteUserDialog((prev) => ({ ...prev, deleteData: !prev.deleteData, toMoveUser: event.target.checked ? '' : undefined }));
  };

  const handleToMoveUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteUserDialog((prev) => ({ ...prev, toMoveUser: event.target.value }));
  };

  const handleDeleteUser = async () => {
    const { user, deleteData, toMoveUser } = deleteUserDialog;

    if (!user) {
      return;
    }

    setDeleteUserDialog((prev) => ({ ...prev, loading: true }));
    const userIdToDelete = user.globalUser || (user as { _id?: string })._id || user.id;
    if (!userIdToDelete) {
      setDeleteUserDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog({ code: 400, message: 'Missing user id' }, '/users/legacy/:userId', 'delete');
      return;
    }
    const res = await deleteAccount(userIdToDelete, deleteData, toMoveUser);

    if (!res.error) {
      if (openTenantId) {
        const newPage = tenantUsers.length === 1 && tenantUsersPage > 1 ? tenantUsersPage - 1 : tenantUsersPage;
        fetchTenantUsers(newPage);
      } else {
        const newPage = users.length === 1 && usersPage > 1 ? usersPage - 1 : usersPage;
        fetchUsers(newPage);
      }
      handleCloseConfirmDeleteUserDialog();
      handleOpenSuccessAlert(t('userDeletedSuccessfully'));
    } else {
      setDeleteUserDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/users/legacy/:userId', 'delete');
    }
  };

  /**
   * Create and edit account handlers
   */

  const handleOpenCreateUserDialog = () => {
    setCreateAndEditUserDialog((prev) => ({ ...prev, open: true }));
  };

  const handleOpenEditUserDialog = (user: User) => {
    return () => {
      setCreateAndEditUserDialog((prev) => ({
        ...prev,
        open: true,
        user,
        firstName: user.firstname ?? '',
        lastName: user.lastname ?? '',
        email: user.email ?? '',
        role: user.role ?? '',
        language: user.language ?? '',
      }));
    };
  };

  const handleCloseCreateUserDialog = () => {
    setCreateAndEditUserDialog({
      open: false,
      loading: false,
      user: null,
      firstName: '',
      lastName: '',
      email: '',
      role: Roles.BASIC,
      language: 'de',
      emailInvalid: false,
      emailAlreadyExists: false,
      firstNameInvalid: false,
      lastNameInvalid: false,
      invitingUserInvalid: false,
    });
  };

  const handleUserInfoChange = (field: 'firstName' | 'lastName' | 'email' | 'role' | 'language') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setCreateAndEditUserDialog((prev) => ({ ...prev, [field]: event.target.value }));
    };
  };

  useEffect(() => {
    setCreateAndEditUserDialog((prev) => ({ ...prev, invitingUserInvalid: false, firstNameInvalid: false }));
  }, [createAndEditUserDialog.firstName]);

  useEffect(() => {
    setCreateAndEditUserDialog((prev) => ({ ...prev, invitingUserInvalid: false, lastNameInvalid: false }));
  }, [createAndEditUserDialog.lastName]);

  useEffect(() => {
    setCreateAndEditUserDialog((prev) => ({ ...prev, emailInvalid: false, emailAlreadyExists: false }));
  }, [createAndEditUserDialog.email]);

  const handleCreateUser = async () => {
    setCreateAndEditUserDialog((prev) => ({
      ...prev,
      loading: true,
      emailInvalid: false,
      emailAlreadyExists: false,
      firstNameInvalid: false,
      lastNameInvalid: false,
      invitingUserInvalid: false,
    }));

    const { firstName, lastName, email, role, language } = createAndEditUserDialog;
    const res = await addAccount(
      email.trim(),
      firstName.trim(),
      lastName.trim(),
      openTenantId ?? tenant,
      role ?? Roles.MANAGER,
      language ?? 'de',
      `${firstName.trim()} ${lastName.trim()}`
    );

    if (res.error) {
      const validationErrorMessage = (res as any).error?.errors?.body?.[0]?.message;
      const validationErrorQueryMessage = (res as any).error?.errors?.query?.[0]?.message;

      if (res.error.message === ErrorMessages.USER_ALREADY_EXISTS) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, emailAlreadyExists: true }));
      } else if (validationErrorQueryMessage === ErrorMessages.INVITING_USER_LENGTH_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, invitingUserInvalid: true }));
      } else if (validationErrorMessage === ErrorMessages.FIRSTNAME_LENGTH_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, firstNameInvalid: true }));
      } else if (validationErrorMessage === ErrorMessages.LASTNAME_LENGTH_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, lastNameInvalid: true }));
      } else if (validationErrorMessage === ErrorMessages.EMAIL_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, emailInvalid: true }));
      } else {
        handleOpenErrorDialog(res?.error, '/users/legacy', 'put');
      }

      setCreateAndEditUserDialog((prev) => ({ ...prev, loading: false }));
      return;
    }

    if (openTenantId) {
      fetchTenantUsers(1);
    } else {
      fetchUsers(1);
    }

    handleCloseCreateUserDialog();
    handleOpenSuccessAlert(t('userCreatedSuccessfully'));

    // Show password-set link dialog if the backend returned a link
    if (res.resetLink) {
      const { firstName: fn, lastName: ln } = createAndEditUserDialog;
      setPasswordLinkDialog({
        open: true,
        resetLink: res.resetLink,
        userName: `${fn} ${ln}`.trim(),
      });
    }

    if (userRole === Roles.MANAGER || userRole === Roles.ADMIN || userRole === Roles.AGENCY || userRole === Roles.OWNER) {
      handleOpenAddLicenseToUserDialog(res.globalUser, openTenantId ?? tenant)();
    }
  };

  const handleUpdateUser = async () => {
    const { user, firstName, lastName, email, role, language } = createAndEditUserDialog;

    if (!user) {
      return;
    }

    setCreateAndEditUserDialog((prev) => ({
      ...prev,
      loading: true,
      emailInvalid: false,
      emailAlreadyExists: false,
      firstNameInvalid: false,
      lastNameInvalid: false,
      invitingUserInvalid: false,
    }));

    const editAccountPayload = {
      firstname: firstName.trim(),
      lastname: lastName.trim(),
      email: email.trim(),
      association: openTenantId ?? tenant,
      role: role,
      language: language,
    };
    // Defensive: fall back to `_id`/`id` if `globalUser` is missing. Prevents
    // `/users/legacy/undefined` requests when a tenant-user list returned raw
    // model shapes without the legacy `globalUser` alias.
    const userId = user.globalUser || (user as { _id?: string })._id || user.id;
    if (!userId) {
      setCreateAndEditUserDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog({ code: 400, message: 'Missing user id' }, '/users/legacy/:userId', 'post');
      return;
    }
    const res = await editAccount(userId, editAccountPayload);

    if (res.error) {
      const validationErrorMessage = (res as any).error?.errors?.body?.[0]?.message;
      const validationErrorMessageAlt = (res as any).error?.errors?.[0]?.messages?.[0];
      if (res.error.message === ErrorMessages.USER_ALREADY_EXISTS || validationErrorMessageAlt === ErrorMessages.EMAIL_ALREADY_EXISTS) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, emailAlreadyExists: true }));
      } else if (validationErrorMessage === ErrorMessages.EMAIL_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, emailInvalid: true }));
      } else if (validationErrorMessage === ErrorMessages.FIRSTNAME_LENGTH_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, firstNameInvalid: true }));
      } else if (validationErrorMessage === ErrorMessages.LASTNAME_LENGTH_VALIDATION_ERROR) {
        setCreateAndEditUserDialog((prev) => ({ ...prev, lastNameInvalid: true }));
      } else {
        handleOpenErrorDialog(res?.error, '/users/legacy/:userId', 'post');
      }
      setCreateAndEditUserDialog((prev) => ({ ...prev, loading: false }));
      return;
    }

    if (openTenantId) {
      fetchTenantUsers(1);
    } else {
      fetchUsers(1);
    }

    handleCloseCreateUserDialog();
    handleOpenSuccessAlert(t('userUpdatedSuccessfully'));
  };

  const handleResendInvitation = (user: User) => {
    return async () => {
      const userId = user.globalUser || (user as { _id?: string })._id || user.id;
      if (!userId) {
        handleOpenErrorDialog({ code: 400, message: 'Missing user id' }, '/users/legacy/:userId/resend-invitation', 'post');
        return;
      }

      setResendingInvitationUserId(userId);
      const res = await resendInvitation(userId, openTenantId ?? tenant);
      setResendingInvitationUserId(null);

      if (res.error) {
        handleOpenErrorDialog(res.error, '/users/legacy/:userId/resend-invitation', 'post');
        return;
      }

      if (openTenantId) {
        fetchTenantUsers(tenantUsersPage);
      } else {
        fetchUsers(usersPage);
      }

      if (res.resetLink) {
        setPasswordLinkDialog({
          open: true,
          resetLink: res.resetLink,
          userName: `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim(),
        });
      }

      handleOpenSuccessAlert(t('invitationResentSuccessfully'));
    };
  };

  /**
   * Add License to user dialog handlers
   */

  const handleOpenAddLicenseToUserDialog = (accountId: string, tenantId: string) => {
    return async () => {
      setAddLicenseToUserDialog((prev) => ({ ...prev, open: true, licensesLoading: true, accountId, tenantId }));

      const res = await getLicensesByCompany(tenantId);

      if (!res.error) {
        const activeLicenses = res.licenses.filter((license: License) => !license.isExpired);

        if (activeLicenses.length > 0) {
          setAddLicenseToUserDialog((prev) => ({ ...prev, allLicenses: activeLicenses }));
        } else {
          handleCloseAddLicenseToUserDialog();
        }
      } else {
        handleOpenErrorDialog(res?.error, `/company/${tenantId}/licenses`, 'get');
      }

      setAddLicenseToUserDialog((prev) => ({ ...prev, licensesLoading: false }));
    };
  };

  const handleCloseAddLicenseToUserDialog = () => {
    setAddLicenseToUserDialog({
      open: false,
      loading: false,
      licensesLoading: false,
      accountId: null,
      tenantId: null,
      allLicenses: [],
      selectedLicenses: [],
    });
  };

  const handleSelectedLicensesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setAddLicenseToUserDialog((prev) => ({
      ...prev,
      selectedLicenses: prev.selectedLicenses.includes(value)
        ? prev.selectedLicenses.filter((license) => license !== value)
        : [...prev.selectedLicenses, value],
    }));
  };

  const handleAddLicenses = async () => {
    // TODO: User-license assignment was removed in the simplified license model.
    // This functionality needs to be re-implemented when the new backend supports it.
    handleCloseAddLicenseToUserDialog();
  };

  /**
   * Manage user licenses handlers
   */

  const handleOpenManageUserLicensesDialog = (user: User) => {
    return async () => {
      const { globalUser, tenant, licenses } = user;
      const tenantId = typeof tenant === 'string' ? tenant : tenant._id;

      if (_.isNil(tenantId)) {
        return;
      }

      setManageUserLicensesDialog((prev) => ({ ...prev, open: true, licensesLoading: true, accountId: globalUser, tenantId }));

      const res = await getLicensesByCompany(tenantId);

      if (!res.error) {
        const activeLicenses = res.licenses.filter((license: License) => !license.isExpired);

        if (activeLicenses.length > 0) {
          setManageUserLicensesDialog((prev) => ({
            ...prev,
            allLicenses: activeLicenses,
            selectedLicenses: licenses?.map((lic) => lic._id) || [],
          }));
        } else {
          handleCloseManageUserLicensesDialog();
        }
      } else {
        handleOpenErrorDialog(res?.error, `/company/${tenantId}/licenses`, 'get');
      }

      setManageUserLicensesDialog((prev) => ({ ...prev, licensesLoading: false }));
    };
  };

  const handleCloseManageUserLicensesDialog = () => {
    setManageUserLicensesDialog({
      open: false,
      loading: false,
      licensesLoading: false,
      accountId: null,
      tenantId: null,
      selectedLicenses: [],
      allLicenses: [],
    });
  };

  const handleManageUserLicensesSelectedLicensesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setManageUserLicensesDialog((prev) => ({
      ...prev,
      selectedLicenses: prev.selectedLicenses.includes(value)
        ? prev.selectedLicenses.filter((license) => license !== value)
        : [...prev.selectedLicenses, value],
    }));
  };

  const handleSaveUserLicenses = async () => {
    // TODO: User-license assignment was removed in the simplified license model.
    // This functionality needs to be re-implemented when the new backend supports it.
    handleCloseManageUserLicensesDialog();
  };

  /**
   * Manage Tenants handlers
   */

  /**
   * Delete Tenant handlers
   */

  const handleOpenDeleteTenantDialog = (tenant: Company) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setDeleteTenantDialog((prev) => ({ ...prev, open: true, tenant }));
    };
  };

  const handleOpenAgencyDialog = (tenant: Company) => {
    return async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setAgencyBindingDialog((prev) => ({
        ...prev,
        open: true,
        loading: true,
        tenant,
        selectedAgencyTenantId: tenant.agencyOwnerTenantId || null,
      }));

      const res = await getAgencyAdmins();
      if (!res.error) {
        const options = (res.data?.agencyAdminUsers || []).map((u: any) => ({
          id: u.tenant?.id || u.id,
          tenantName: u.tenant?.name || '',
          userName: `${u.firstname || ''} ${u.lastname || ''}`.trim(),
          email: u.email,
        }));
        setAgencyBindingDialog((prev) => ({ ...prev, agencyOptions: options, loading: false }));
      } else {
        setAgencyBindingDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res?.error, '/v1/agency-management/agency-admins', 'get');
      }
    };
  };

  const handleCloseAgencyDialog = () => {
    setAgencyBindingDialog({ open: false, loading: false, tenant: null, selectedAgencyTenantId: null, agencyOptions: [] });
  };

  const handleChangeSelectedAgency = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgencyBindingDialog((prev) => ({ ...prev, selectedAgencyTenantId: event.target.value }));
  };

  const handleUnbindAgency = async () => {
    const { tenant } = agencyBindingDialog;
    if (!tenant) return;
    setAgencyBindingDialog((prev) => ({ ...prev, loading: true }));
    const res = await unbindAccountFromAgency(tenant.id);
    if (!res.error) {
      handleCloseAgencyDialog();
      handleOpenSuccessAlert(t('savedSuccessfully'));
      fetchCompanies();
    } else {
      setAgencyBindingDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/v1/agency-management/unbind', 'post');
    }
  };

  const handleSaveAgencyBinding = async () => {
    const { tenant, selectedAgencyTenantId } = agencyBindingDialog;
    if (!tenant || !selectedAgencyTenantId) return;
    setAgencyBindingDialog((prev) => ({ ...prev, loading: true }));
    const res = await bindAccountToAgency(tenant.id, selectedAgencyTenantId);
    if (!res.error) {
      handleCloseAgencyDialog();
      handleOpenSuccessAlert(t('savedSuccessfully'));
      fetchCompanies();
    } else {
      setAgencyBindingDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/v1/agency-management/bind', 'post');
    }
  };

  const handleCloseDeleteTenantDialog = () => {
    setDeleteTenantDialog({ open: false, loading: false, tenant: null });
  };

  const handleDeleteTenant = async () => {
    const { tenant } = deleteTenantDialog;

    if (_.isNil(tenant)) {
      return;
    }

    setDeleteTenantDialog((prev) => ({ ...prev, loading: true }));

    const res = await deleteCompany(tenant.id);

    if (res && !res.error) {
      handleCloseDeleteTenantDialog();
      handleOpenSuccessAlert(t('tenantDeletedSuccessfully'));
      fetchCompanies();
    } else {
      handleOpenErrorDialog(res?.error, '/tenants/:tenantId', 'delete');
    }
  };

  /**
   * Create and edit tenant handlers
   */

  const handleOpenCreateTenantDialog = () => {
    setCreateAndEditTenantDialog((prev) => ({ ...prev, open: true }));
  };

  const handleOpenEditTenantDialog = (tenant: Company) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setCreateAndEditTenantDialog((prev) => ({
        ...prev,
        open: true,
        tenant,
        name: tenant.name ?? '',
        addressLine1: tenant.addressLine1 ?? '',
        postalCode: tenant.postalCode ?? '',
        city: tenant.city ?? '',
      }));
    };
  };

  const handleCloseCreateTenantDialog = () => {
    setCreateAndEditTenantDialog({ open: false, loading: false, tenant: null, name: '', addressLine1: '', postalCode: '', city: '' });
  };

  const handleTenantInfoChange = (field: 'name' | 'addressLine1' | 'postalCode' | 'city') => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setCreateAndEditTenantDialog((prev) => ({ ...prev, [field]: event.target.value }));
    };
  };

  const handleCreateTenant = async () => {
    const { name, addressLine1, postalCode, city } = createAndEditTenantDialog;

    const updatePayload: Company = new Company({
      name: name,
      addressLine1: addressLine1,
      postalCode: postalCode,
      city: city,
    });

    setCreateAndEditTenantDialog((prev) => ({ ...prev, loading: true }));
    const res = await addCompany(updatePayload);

    if (!res.error) {
      const newTenantId = res.data._id || res.data.id || null;
      fetchCompanies(undefined, undefined, undefined, undefined, true);
      setOpenTenantId(newTenantId);
      handleCloseCreateTenantDialog();
      handleOpenSuccessAlert(t('tenantCreatedSuccessfully'));
    } else {
      handleOpenErrorDialog(res?.error, '/tenants', 'post');
      setCreateAndEditTenantDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleUpdateTenant = async () => {
    const { tenant, name, addressLine1, postalCode, city } = createAndEditTenantDialog;

    if (_.isNil(tenant)) {
      return;
    }

    const updatePayload: Company = new Company({
      name: name,
      addressLine1: addressLine1,
      postalCode: postalCode,
      city: city,
    });

    setCreateAndEditTenantDialog((prev) => ({ ...prev, loading: true }));
    const res = await changeCompanyData(tenant.id, updatePayload);

    if (res && !res.error) {
      fetchCompanies();
      handleCloseCreateTenantDialog();
      handleOpenSuccessAlert(t('tenantUpdatedSuccessfully'));
    } else {
      handleOpenErrorDialog(res?.error, '/tenants/:tenantId', 'patch');
      setCreateAndEditTenantDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * Tenant users handlers
   */

  const handleOpenTenant = (tenant: Company) => {
    return () => {
      setOpenTenantId((prev) => (prev === tenant.id ? null : tenant.id));
    };
  };

  function resetOpenTenantId() {
    setOpenTenantId(null);
  }

  /**
   * Admin login dialog handlers
   */

  const handleOpenAdminSudoLoginDialog = (account: User) => {
    return () => {
      if (userRole === Roles.ADMIN || userRole === Roles.AGENCY || userRole === Roles.OWNER) {
        const tenantId =
          typeof account.tenant === 'string'
            ? account.tenant
            : account.tenant?._id || account.tenant?.id || '';
        setAdminLoginDialog((prev) => ({
          ...prev,
          open: true,
          adminEmail: userEmail ?? '',
          userEmail: account.email,
          tenantId,
        }));
      }
    };
  };

  const handleCloseAdminSudoLoginDialog = () => {
    setAdminLoginDialog({
      open: false,
      loading: false,
      userEmail: '',
      tenantId: '',
      adminEmail: '',
      password: '',
      helperTextUsername: '',
      helperTextPassword: '',
    });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdminLoginDialog((prev) => ({ ...prev, password: e.target.value }));
  };

  const handleSignIn = async () => {
    const { userEmail, adminEmail, password, tenantId } = adminLoginDialog;
    if (!isEmail(userEmail)) {
      setAdminLoginDialog((prev) => ({ ...prev, helperTextUsername: '* ' + t('noValidEmailError') }));
      return;
    }

    setAdminLoginDialog((prev) => ({ ...prev, loading: true }));

    const loginRes = await sudoLogin(userEmail, adminEmail, password, tenantId || undefined);

    if (loginRes.error) {
      if (loginRes.error.code === 400 && loginRes.error.message === ErrorMessages.USER_NOT_EXISTS) {
        setAdminLoginDialog((prev) => ({ ...prev, helperTextUsername: '* ' + t('userNotFound') }));
      } else if (loginRes.error.code === 401 && loginRes.error.message === ErrorMessages.WRONG_PASSWORD) {
        setAdminLoginDialog((prev) => ({ ...prev, helperTextPassword: '* ' + t('makeSurePasswordCorrect') }));
      } else {
        handleOpenErrorDialog(loginRes?.error, '/auth/sudo-login', 'post');
      }
      setAdminLoginDialog((prev) => ({ ...prev, loading: false }));
      return;
    }

    if (loginRes.data) {
      // Apply sudo token immediately so following API calls use the impersonated user context.
      setToken(loginRes.data.token);
      const [userInfoRes, productsRes] = await Promise.all([getUserInfo(), getAvailableProducts()]);

      if (!userInfoRes.error && !productsRes.error) {
        const userInfo: UserInfo = new UserInfo(userInfoRes);
        const products = productsRes.products;
        signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
      }

      if (productsRes.error && !userInfoRes.error) {
        const userInfo: UserInfo = new UserInfo(userInfoRes);
        const decoded: any = jwt.decode(loginRes.data.token.accessToken, { complete: true });
        if (decoded && decoded.payload && decoded.payload.possessedLicenses) {
          const products: Products[] = decoded.payload.possessedLicenses;
          signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
        }
      }

      if (userInfoRes.error) {
        handleOpenErrorDialog(userInfoRes?.error, '/users/me', 'get');
      }

      i18n.changeLanguage(loginRes.data.user.language);
      extensionSignIn(loginRes.data.token, loginRes.data.user);

      handleCloseAdminSudoLoginDialog();
      localStorage.setItem('adminEmail', adminEmail);
      // Full page load, not a client-side navigate: everything the app had loaded for the
      // admin (menu, role, licenses, open lists) would otherwise linger in the customer's
      // workspace. Leaving the impersonation already reloads; entering now does too.
      window.location.assign(`/dashboard${Routes.AI_CHAT}`);
      return;
    }

    setAdminLoginDialog((prev) => ({ ...prev, loading: false }));
  };

  /**
   * Linkedin account preview dialog handlers
   */

  const abortController = useRef<AbortController | null>(null);

  const resetAbortController = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
  };

  const handleCloseLinkedinAccountPreviewDialog = () => {
    setLinkedinAccountPreviewDialog({ open: false, loading: false, linkedinAccounts: [] });
    resetAbortController();
  };

  /**
   * Manage license users dialog handlers
   */

  const handleOpenManageLicenseUsersDialog = (license: License, stripeProduct: Products | null = null) => {
    return async () => {
      const userIds: string[] = [];

      setManageLicenseUsersDialog((prev) => ({ ...prev, open: true, stripeProduct, allUsersLoading: true, license, licenseUsers: userIds }));
      const res = await getAllUsers(undefined, undefined, undefined, undefined, undefined, ViewModes.MANAGER);

      if (!res.error) {
        setManageLicenseUsersDialog((prev) => ({ ...prev, allUsers: res.data.users, allUsersLoading: false }));
      } else {
        handleOpenErrorDialog(res?.error, '/users/legacy', 'get');
        setManageLicenseUsersDialog((prev) => ({ ...prev, allUsersLoading: false }));
        return;
      }
    };
  };

  const handleCloseManageLicenseUsersDialog = () => {
    setManageLicenseUsersDialog({
      open: false,
      loading: false,
      stripeProduct: null,
      license: null,
      licenseUsers: [],
      allUsers: [],
      allUsersLoading: false,
    });
  };

  const handleSelectedLicenseUsersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const { licenseUsers } = manageLicenseUsersDialog;

    if (licenseUsers.includes(value)) {
      setManageLicenseUsersDialog((prev) => ({ ...prev, licenseUsers: licenseUsers.filter((id) => id !== value) }));
    } else {
      setManageLicenseUsersDialog((prev) => ({ ...prev, licenseUsers: [...licenseUsers, value] }));
    }
  };

  const handleSelectAllLicenseUsers = () => {
    const { allUsers } = manageLicenseUsersDialog;
    setManageLicenseUsersDialog((prev) => ({ ...prev, licenseUsers: allUsers.map((user) => user.globalUser) }));
  };

  const handleUnselectAllLicenseUsers = () => {
    setManageLicenseUsersDialog((prev) => ({ ...prev, licenseUsers: [] }));
  };

  const handleSaveLicenseUsers = async () => {
    const { license } = manageLicenseUsersDialog;

    if (_.isNil(license)) {
      return;
    }

    // TODO: User-license assignment was removed in the simplified license model.
    // This functionality needs to be re-implemented when the new backend supports it.
    handleCloseManageLicenseUsersDialog();
  };

  /**
   * Manage subscription handler
   */

  /** Kündigen: first the dialog (results, reason, offer); its "Kündigen" continues to the portal. */
  const [cancelSubscriptionOpen, setCancelSubscriptionOpen] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const handleOpenCancelSubscription = () => () => setCancelSubscriptionOpen(true);
  const handleCancelSubscriptionContinue = async () => {
    setOpeningPortal(true);
    await handleManageSubscription()();
    setOpeningPortal(false);
  };

  const handleManageSubscription = () => {
    return async () => {
      const res = await createCustomerPortalSession();
      if (!res.error && res.sessionUrl) {
        window.location.href = res.sessionUrl;
      } else {
        handleOpenErrorDialog(res?.error as any, '/payments/customer-portal-session', 'post');
      }
    };
  };

  /**
   * Cancel license dialog handlers
   */

  const handleOpenCancelLicenseDialog = (licenseId: string) => {
    return () => {
      setCancelLicenseDialog((prev) => ({ ...prev, open: true, licenseId }));
    };
  };

  const handleCloseCancelLicenseDialog = () => {
    setCancelLicenseDialog({ open: false, loading: false, licenseId: null });
  };

  const handleCancelLicense = async () => {
    const { licenseId } = cancelLicenseDialog;

    if (_.isNil(licenseId)) return;

    setCancelLicenseDialog((prev) => ({ ...prev, loading: true }));

    const res = await cancelLicense(licenseId);
    if (!res.error) {
      fetchLicenses();
      handleOpenSuccessAlert(t('cancelSubscriptionSuccess'));
      handleCloseCancelLicenseDialog();
    } else {
      setCancelLicenseDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/license/:licenseId/renew', 'put');
    }
  };

  /**
   * Tenant licenses handlers
   */

  const handleToggleTenantScope = () => {
    setTenantScope((prev) => (prev === TenantScopes.USERS ? TenantScopes.LICENSES : TenantScopes.USERS));
  };

  /**
   * Admin license dialog handlers
   */

  const handleOpenAdminCreateLicenseDialog = (tenantId: string) => {
    return async () => {
      setAdminLicenseDialog((prev) => ({ ...prev, open: true, companyId: tenantId }));

      const [productsRes, companyDataRes] = await Promise.all([getProducts(), getCompanyData(tenantId)]);

      if (!productsRes.error) {
        setAdminLicenseDialog((prev) => ({ ...prev, products: productsRes }));
      } else {
        handleOpenErrorDialog(productsRes?.error, '/licenses/products', 'get');
      }

      if (companyDataRes.error) {
        handleOpenErrorDialog(companyDataRes?.error, '/tenants/:tenantId', 'get');
      }
    };
  };

  const handleOpenAdminEditLicenseDialog = (tenantId: string) => (license: License) => {
    return async () => {
      const { product, billingType, expiresAt } = license;

      setAdminLicenseDialog((prev) => ({
        ...prev,
        open: true,
        license: license,
        companyId: tenantId,
        product: product,
        billingType: billingType || 'yearly',
        credits: license.credits ?? 0,
        expirationDate: expiresAt ? new Date(expiresAt) : addMonths(new Date(), 1),
      }));

      const [productsRes, companyDataRes] = await Promise.all([getProducts(), getCompanyData(tenantId)]);

      if (!productsRes.error) {
        setAdminLicenseDialog((prev) => ({ ...prev, products: productsRes }));
      } else {
        handleOpenErrorDialog(productsRes?.error, '/licenses/products', 'get');
      }

      if (companyDataRes.error) {
        handleOpenErrorDialog(companyDataRes?.error, '/tenants/:tenantId', 'get');
      }
    };
  };

  const handleCloseAdminLicenseDialog = () => {
    setAdminLicenseDialog({
      open: false,
      loading: false,
      license: null,
      companyId: null,
      product: '',
      billingType: 'yearly',
      credits: 0,
      expirationDate: addMonths(new Date(), 1),
      products: [],
    });
  };

  const handleLicenseProductChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdminLicenseDialog((prev) => ({ ...prev, product: event.target.value }));
  };

  const handleLicenseBillingTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdminLicenseDialog((prev) => ({ ...prev, billingType: event.target.value as 'monthly' | 'quarterly' | 'yearly' | 'one_time' }));
  };

  const handleLicenseCreditsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAdminLicenseDialog((prev) => ({ ...prev, credits: +event.target.value }));
  };

  const handleLicenseExpDateChange = (date: Date | null) => {
    setAdminLicenseDialog((prev) => ({ ...prev, expirationDate: date || new Date() }));
  };

  const handleCreateNewLicense = async () => {
    const { companyId, product, billingType, credits, expirationDate } = adminLicenseDialog;

    if (!companyId || !product) return;

    setAdminLicenseDialog((prev) => ({ ...prev, loading: true }));

    const isCreditProduct = [Products.MOTIVE, Products.LEAD].includes(product as Products);

    const res = await createLicense({
      tenantId: companyId,
      product,
      billingType,
      credits: isCreditProduct ? credits : null,
      expiresAt: expirationDate ? expirationDate.toISOString() : null,
    });

    if (!('error' in res)) {
      fetchLicenses();
      // Newest first — page 1 is where the new seat shows up in the open tenant.
      fetchTenantLicenses(1);
      refreshGlobalLicenses();
      handleOpenSuccessAlert(t('licenseCreatedSuccessfully') || 'License created successfully');
      handleCloseAdminLicenseDialog();
    } else {
      setAdminLicenseDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error as CustomError | undefined, '/licenses', 'post');
    }
  };

  const handleUpdateLicense = async () => {
    const { license, product, billingType, credits, expirationDate } = adminLicenseDialog;

    if (!license) return;

    setAdminLicenseDialog((prev) => ({ ...prev, loading: true }));

    const isCreditProduct = [Products.MOTIVE, Products.LEAD].includes(product as Products);

    const res = await editLicense(license._id, {
      product: product || undefined,
      billingType,
      credits: isCreditProduct ? credits : null,
      expiresAt: expirationDate ? expirationDate.toISOString() : null,
    });

    if (!('error' in res)) {
      fetchLicenses();
      fetchTenantLicenses();
      refreshGlobalLicenses();
      handleOpenSuccessAlert(t('licenseUpdatedSuccessfully') || 'License updated successfully');
      handleCloseAdminLicenseDialog();
    } else {
      setAdminLicenseDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error as CustomError | undefined, '/licenses/:licenseId', 'patch');
    }
  };

  const handleOpenDeleteLicenseDialog = (license: License) => {
    return () => {
      setDeleteLicenseDialog((prev) => ({ ...prev, open: true, license }));
    };
  };

  const handleCloseDeleteLicenseDialog = () => {
    setDeleteLicenseDialog({ open: false, loading: false, license: null });
  };

  const handleDeleteLicense = async () => {
    const { license } = deleteLicenseDialog;
    if (_.isNil(license)) return;

    setDeleteLicenseDialog((prev) => ({ ...prev, loading: true }));
    const res = await deleteLicense(license._id);

    if (!res.error) {
      fetchTenantLicenses();
      refreshGlobalLicenses();
      handleCloseDeleteLicenseDialog();
      handleOpenSuccessAlert(t('licenseDeletedSuccessfully'));
    } else {
      setDeleteLicenseDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/license/:licenseId', 'delete');
    }
  };

  /**
   * Stripe Success
   */

  useEffect(() => {
    if (stripeProduct && (userRole === Roles.ADMIN || userRole === Roles.AGENCY || userRole === Roles.MANAGER || userRole === Roles.OWNER)) {
      setActiveTab(AccountManagementTabs.LICENSES);
      fetchLicenses();

      if (!import.meta.env.DEV) {
        (async () => {
          const resRefresh = await refreshTokenCall();

          if (!resRefresh.error && !_.isNil(resRefresh.accessToken)) {
            const resProducts = await getAvailableProducts();

            if (!resProducts.error && !_.isNil(resProducts.products)) {
              const newProducts = resProducts.products;
              setProducts(newProducts);
            } else {
              handleOpenErrorDialog(resProducts?.error, '/licenses/current/products', 'get');
            }
          } else {
            handleOpenErrorDialog(resRefresh?.error, '/auth/refresh', 'post');
          }
        })();
      }

      // Track successful payment with real product and pricing data
      (async () => {
        try {
          let actualValue: number | undefined;
          let actualCurrency: string | undefined;

          // Get actual pricing data for the purchased product
          if (stripeProduct && prices) {
            const productPricing = prices[stripeProduct];
            actualCurrency = prices.currency;

            // Try to determine the actual price paid (prefer yearly, fallback to monthly)
            if (productPricing) {
              actualValue =
                productPricing.yearly || productPricing.monthly;
            }
          }

          await trackPurchaseComplete(sessionId, stripeProduct || undefined, actualValue, actualCurrency);
        } catch (error) {
          console.error('Payment tracking failed:', error);
        }
      })();
    }
  }, [stripeProduct, userRole, sessionId]);

  useEffect(() => {
    const hasManagementRole =
      userRole === Roles.ADMIN || userRole === Roles.AGENCY || userRole === Roles.MANAGER || userRole === Roles.OWNER;
    if (stripeProduct && !_.isEmpty(licenses) && hasManagementRole) {
      const firstLicense = _.first(licenses) as License;
      if (firstLicense) {
        handleOpenManageLicenseUsersDialog(firstLicense, stripeProduct)();
        setSearchParams({}, { replace: true });
      }
    }
  }, [stripeProduct, licenses, userRole]);

  return (
    <>
      <div className={classes.accountManagement}>
        <div className={classes.main}>
          <div className={classes.pageHeader}>
            <Typography className={classes.pageTitle}>{t(TAB_HEADERS[activeTab].title)}</Typography>
            <Typography className={classes.pageSubtitle}>{t(TAB_HEADERS[activeTab].subtitle)}</Typography>
          </div>
          <div className={classes.content}>{renderContent()}</div>
        </div>
      </div>

      <CancelSubscriptionDialog
        open={cancelSubscriptionOpen}
        onClose={() => setCancelSubscriptionOpen(false)}
        onCancel={handleCancelSubscriptionContinue}
        cancelling={openingPortal}
      />
      <DeleteUserDialog
        open={deleteUserDialog.open}
        user={deleteUserDialog.user}
        deleteData={deleteUserDialog.deleteData}
        tenantUsers={deleteUserDialog.tenantUsers}
        toMoveUser={deleteUserDialog.toMoveUser}
        loading={deleteUserDialog.loading}
        userRole={userRole}
        handleDeleteDataChange={handleDeleteUserDataChange}
        handleToMoveUserChange={handleToMoveUserChange}
        handleConfirm={handleDeleteUser}
        handleClose={handleCloseConfirmDeleteUserDialog}
      />
      <CreateAndEditUserDialog
        open={createAndEditUserDialog.open}
        loading={createAndEditUserDialog.loading}
        user={createAndEditUserDialog.user}
        firstName={createAndEditUserDialog.firstName}
        lastName={createAndEditUserDialog.lastName}
        email={createAndEditUserDialog.email}
        role={createAndEditUserDialog.role}
        language={createAndEditUserDialog.language}
        emailInvalid={createAndEditUserDialog.emailInvalid}
        emailAlreadyExists={createAndEditUserDialog.emailAlreadyExists}
        firstNameInvalid={createAndEditUserDialog.firstNameInvalid}
        lastNameInvalid={createAndEditUserDialog.lastNameInvalid}
        invitingUserInvalid={createAndEditUserDialog.invitingUserInvalid}
        handleUserInfoChange={handleUserInfoChange}
        handleClose={handleCloseCreateUserDialog}
        createUser={handleCreateUser}
        updateUser={handleUpdateUser}
      />
      <AddLicenseToUserDialog
        open={addLicenseToUserDialog.open}
        loading={addLicenseToUserDialog.loading}
        licensesLoading={addLicenseToUserDialog.licensesLoading}
        licenses={addLicenseToUserDialog.allLicenses}
        selectedLicenses={addLicenseToUserDialog.selectedLicenses}
        handleSelectedLicensesChange={handleSelectedLicensesChange}
        handleAddLicenses={handleAddLicenses}
        handleClose={handleCloseAddLicenseToUserDialog}
      />
      <ManageUserLicensesDialog
        open={manageUserLicensesDialog.open}
        loading={manageUserLicensesDialog.loading}
        licensesLoading={manageUserLicensesDialog.licensesLoading}
        licenses={manageUserLicensesDialog.allLicenses}
        selectedLicenses={manageUserLicensesDialog.selectedLicenses}
        handleSelectedLicensesChange={handleManageUserLicensesSelectedLicensesChange}
        handleSaveLicenses={handleSaveUserLicenses}
        handleClose={handleCloseManageUserLicensesDialog}
      />
      <DeleteTenantDialog
        open={deleteTenantDialog.open}
        loading={deleteTenantDialog.loading}
        tenant={deleteTenantDialog.tenant}
        handleDeleteTenant={handleDeleteTenant}
        handleClose={handleCloseDeleteTenantDialog}
      />
      <CreateAndEditTenantDialog
        open={createAndEditTenantDialog.open}
        loading={createAndEditTenantDialog.loading}
        tenant={createAndEditTenantDialog.tenant}
        name={createAndEditTenantDialog.name}
        addressLine1={createAndEditTenantDialog.addressLine1}
        postalCode={createAndEditTenantDialog.postalCode}
        city={createAndEditTenantDialog.city}
        handleTenantInfoChange={handleTenantInfoChange}
        handleClose={handleCloseCreateTenantDialog}
        createTenant={handleCreateTenant}
        updateTenant={handleUpdateTenant}
      />
      <AgencyBindingDialog
        open={agencyBindingDialog.open}
        loading={agencyBindingDialog.loading}
        tenantName={agencyBindingDialog.tenant?.name}
        currentAgencyName={agencyBindingDialog.tenant?.agencyOwnerTenantName}
        selectedAgencyTenantId={agencyBindingDialog.selectedAgencyTenantId}
        agencyOptions={agencyBindingDialog.agencyOptions}
        onChangeSelectedAgency={handleChangeSelectedAgency}
        onUnbind={handleUnbindAgency}
        onSave={handleSaveAgencyBinding}
        onClose={handleCloseAgencyDialog}
      />
      <AdminLoginDialog
        open={adminLoginDialog.open}
        loading={adminLoginDialog.loading}
        adminEmail={adminLoginDialog.adminEmail}
        userEmail={adminLoginDialog.userEmail}
        password={adminLoginDialog.password}
        helperTextUsername={adminLoginDialog.helperTextUsername}
        helperTextPassword={adminLoginDialog.helperTextPassword}
        handlePasswordChange={handlePasswordChange}
        handleSignIn={handleSignIn}
        handleClose={handleCloseAdminSudoLoginDialog}
      />
      <LinkedinAccountPreviewDialog
        open={linkedinAccountPreviewDialog.open}
        loading={linkedinAccountPreviewDialog.loading}
        linkedinAccounts={linkedinAccountPreviewDialog.linkedinAccounts}
        handleClose={handleCloseLinkedinAccountPreviewDialog}
      />
      <ManageLicenseUsersDialog
        open={manageLicenseUsersDialog.open}
        loading={manageLicenseUsersDialog.loading}
        stripeProduct={manageLicenseUsersDialog.stripeProduct}
        licenseUsers={manageLicenseUsersDialog.licenseUsers}
        allUsers={manageLicenseUsersDialog.allUsers}
        allUsersLoading={manageLicenseUsersDialog.allUsersLoading}
        handleSelectedLicenseUsersChange={handleSelectedLicenseUsersChange}
        handleSelectAllLicenseUsers={handleSelectAllLicenseUsers}
        handleUnselectAllLicenseUsers={handleUnselectAllLicenseUsers}
        handleClose={handleCloseManageLicenseUsersDialog}
        handleSaveLicenseUsers={handleSaveLicenseUsers}
      />
      <CancelLicenseDialog
        open={cancelLicenseDialog.open}
        loading={cancelLicenseDialog.loading}
        handleClose={handleCloseCancelLicenseDialog}
        handleCancelLicense={handleCancelLicense}
      />
      <AdminLicenseDialog
        open={adminLicenseDialog.open}
        license={adminLicenseDialog.license}
        loading={adminLicenseDialog.loading}
        products={adminLicenseDialog.products}
        selectedProduct={adminLicenseDialog.product}
        selectedBillingType={adminLicenseDialog.billingType}
        credits={adminLicenseDialog.credits}
        expirationDate={adminLicenseDialog.expirationDate}
        handleProductChange={handleLicenseProductChange}
        handleBillingTypeChange={handleLicenseBillingTypeChange}
        handleCreditsChange={handleLicenseCreditsChange}
        handleExpDateChange={handleLicenseExpDateChange}
        handleCreateNewLicense={handleCreateNewLicense}
        handleUpdateLicense={handleUpdateLicense}
        handleClose={handleCloseAdminLicenseDialog}
      />
      <DeleteLicenseDialog
        open={deleteLicenseDialog.open}
        loading={deleteLicenseDialog.loading}
        license={deleteLicenseDialog.license}
        handleDeleteLicense={handleDeleteLicense}
        handleClose={handleCloseDeleteLicenseDialog}
      />
      <PasswordLinkDialog
        open={passwordLinkDialog.open}
        resetLink={passwordLinkDialog.resetLink}
        userName={passwordLinkDialog.userName}
        handleClose={() => setPasswordLinkDialog({ open: false, resetLink: '', userName: '' })}
      />
      <PaiSuccessAlert open={successAlert.open} message={successAlert.message} position={'bottom'} handleClose={handleCloseSuccessAlert} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  accountManagement: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    background: theme.palette.backgrounds.main_new,
    overflow: 'hidden',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  pageHeader: {
    padding: '28px 40px 20px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    [theme.breakpoints.down('md')]: {
      padding: '20px 20px 16px 20px',
    },
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
  },
  pageSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: '0 40px 40px 40px',
    [theme.breakpoints.down('md')]: {
      padding: '0 20px 20px 20px',
    },
  },
}));

export default AccountManagement;
